import {
  CalculatedCostState,
  ListedInstrument,
  Loadable,
  LoadableState,
  OrderCreateRequest,
} from '@eih-netbroker/common';
import { useCallback } from 'react';
import { useOrderState } from '../../components/OrderStateContext';
import { OrderFormData } from '../../components/order/OrderForm';
import { useNormalOrderSchema } from '../../schemas/orderSchemas';
import { ValidatedOrder } from './useNormalOrderView';
import { useOrderManagement } from './useOrderManagement';

export const useOrderConfirmCalculateCost = () => {
  const { updateCalculatedCostState, getOrderTotalPrice, transactionId } = useOrderState();
  const { costCalculation, mapOrderFormToOrderRequest, getTotalPrices } = useOrderManagement();
  const { getNormalOrderSchema } = useNormalOrderSchema();

  const calculateOrderCost = useCallback(
    async (
      instrument: ListedInstrument,
      order: Omit<OrderFormData, 'instrument' | 'expiry'>,
      accountId?: string,
    ): Promise<LoadableState<CalculatedCostState>> => {
      const orderUnvalidated: OrderFormData & Partial<OrderCreateRequest> = {
        ...order,
        instrument,
      };

      const isValid = await getNormalOrderSchema({ ...order, instrument }).isValid(orderUnvalidated, {
        abortEarly: false,
      });

      if (isValid) {
        try {
          const orderValidated = orderUnvalidated as ValidatedOrder;
          updateCalculatedCostState(Loadable.loading);

          const costCalculationResponse = await costCalculation(
            mapOrderFormToOrderRequest({ ...orderValidated, instrument }),
            transactionId,
            accountId,
          );

          const price = await getOrderTotalPrice({ ...orderValidated, instrument });

          const totalPrices = getTotalPrices(costCalculationResponse, price);

          const res: LoadableState<CalculatedCostState> = Loadable.loaded({
            calculatedCost: costCalculationResponse,
            priceUsedInCalculation: price,
            ...totalPrices,
          });
          updateCalculatedCostState(res);
          return res;
        } catch (e) {
          const res = Loadable.failed;
          updateCalculatedCostState(res);
          return res;
        }
      }
      return Loadable.failed;
    },
    [
      costCalculation,
      getTotalPrices,
      getOrderTotalPrice,
      updateCalculatedCostState,
      mapOrderFormToOrderRequest,
      getNormalOrderSchema,
      transactionId,
    ],
  );

  const resetCostCalculation = useCallback(() => {
    updateCalculatedCostState();
  }, [updateCalculatedCostState]);

  return {
    calculateOrderCost,
    resetCostCalculation,
  };
};
